import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Send, Bot, User, Loader2, 
  DollarSign, CheckCircle, AlertCircle, Handshake
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NegotiationPage() {
  const { negotiationId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [negotiation, setNegotiation] = useState(null);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchNegotiation = async () => {
      try {
        const response = await fetch(`${API_URL}/api/negotiations/${negotiationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setNegotiation(data);
        } else {
          toast.error("Negociação não encontrada");
          navigate("/budgets");
        }
      } catch (error) {
        console.error("Error fetching negotiation:", error);
        toast.error("Erro ao carregar negociação");
      } finally {
        setLoading(false);
      }
    };

    fetchNegotiation();
  }, [negotiationId, token, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [negotiation?.messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/negotiations/${negotiationId}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          content: inputMessage,
          message_type: "text"
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setNegotiation(updated);
        setInputMessage("");
      } else {
        throw new Error("Erro ao enviar mensagem");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const agreeNegotiation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/negotiations/${negotiationId}/agree`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const contract = await response.json();
        toast.success("Acordo fechado! Contrato gerado.");
        navigate("/contracts");
      } else {
        throw new Error("Erro ao fechar acordo");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!negotiation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Card className="p-6 text-center">
          <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Negociação não encontrada</p>
          <Button className="mt-4" onClick={() => navigate("/budgets")}>
            Voltar
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid="negotiation-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/budgets")} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <p className="font-semibold text-slate-900">Negociação</p>
                <p className="text-xs text-slate-500">#{negotiationId.slice(-8)}</p>
              </div>
            </div>
            <Badge 
              className={negotiation.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}
            >
              {negotiation.status === "active" ? "Em Andamento" : negotiation.status === "agreed" ? "Acordo Fechado" : negotiation.status}
            </Badge>
          </div>
        </div>
      </header>

      {/* Current Offer Banner */}
      <div className="bg-slate-900 text-white py-3">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <span className="text-sm">Oferta atual:</span>
              <span className="font-bold">
                R$ {negotiation.current_offer?.total?.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) || "0,00"}
              </span>
            </div>
            {negotiation.status === "active" && (
              <Button 
                size="sm" 
                className="bg-emerald-500 hover:bg-emerald-600"
                onClick={agreeNegotiation}
                data-testid="agree-btn"
              >
                <Handshake className="w-4 h-4 mr-2" />
                Fechar Acordo
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 overflow-y-auto">
        <div className="space-y-4">
          {negotiation.messages?.map((message, index) => (
            <div
              key={message.message_id || index}
              className={`flex ${message.sender_role === user?.role ? "justify-end" : "justify-start"} animate-fade-in`}
            >
              <div className={`flex items-start gap-3 max-w-[85%] ${message.sender_role === user?.role ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.sender_role === "ai_mediator" 
                    ? "bg-emerald-100" 
                    : message.sender_role === user?.role 
                      ? "bg-slate-900" 
                      : "bg-slate-200"
                }`}>
                  {message.sender_role === "ai_mediator" ? (
                    <Bot className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <User className={`w-4 h-4 ${message.sender_role === user?.role ? "text-white" : "text-slate-600"}`} />
                  )}
                </div>
                <div>
                  <div className={`rounded-2xl px-4 py-3 ${
                    message.sender_role === "ai_mediator"
                      ? "bg-emerald-50 border border-emerald-200 rounded-bl-md"
                      : message.sender_role === user?.role 
                        ? "bg-slate-900 text-white rounded-br-md" 
                        : "bg-white shadow-sm border border-slate-200 rounded-bl-md"
                  }`}>
                    {message.sender_role === "ai_mediator" && (
                      <p className="text-xs font-medium text-emerald-600 mb-1">Mediador IA</p>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 px-2">
                    {new Date(message.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            </div>
          ))}
          
          {sending && (
            <div className="flex justify-end animate-fade-in">
              <div className="bg-slate-100 rounded-2xl px-4 py-3">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {negotiation.status === "agreed" && (
          <Card className="mt-6 bg-emerald-50 border-emerald-200">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Acordo Fechado!</h3>
              <p className="text-slate-600 mb-4">O contrato foi gerado e está aguardando assinatura.</p>
              <Button onClick={() => navigate("/contracts")}>
                Ver Contrato
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Input Area */}
      {negotiation.status === "active" && (
        <div className="bg-white border-t border-slate-200 sticky bottom-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Textarea
                  placeholder="Digite sua mensagem..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="resize-none min-h-[48px] max-h-32"
                  rows={1}
                  disabled={sending}
                  data-testid="negotiation-input"
                />
              </div>
              <Button 
                onClick={sendMessage}
                disabled={!inputMessage.trim() || sending}
                className="h-12 w-12 p-0 bg-slate-900 hover:bg-slate-800"
                data-testid="send-btn"
              >
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2 text-center">
              Mediação assistida por IA • Mantenha o diálogo respeitoso
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
